const UUID_FILE_BASE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const TOKEN_LABEL = /^[A-Z0-9]+(?:_[A-Z0-9]+)*$/;

/** True when the basename (without extension) looks like a UUID storage key. */
export function isUuidLikeFileName(fileName: string): boolean {
  const trimmed = fileName.trim();
  if (!trimmed) {
    return false;
  }
  const dot = trimmed.lastIndexOf('.');
  const base = dot > 0 ? trimmed.slice(0, dot) : trimmed;
  return UUID_FILE_BASE.test(base);
}

export function toSentenceCase(value: string): string {
  const words = value
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  if (!words) {
    return '';
  }
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function sanitizeOriginalFileName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) {
    return 'upload';
  }
  return trimmed.replace(/[^\w.\- ()]+/g, '_').slice(0, 512);
}

/**
 * Prefer the real client file name when readable.
 * Only substitute a fallback when the name is missing or UUID-like.
 */
export function resolveUploadDisplayName(
  fileName: string,
  fallbackLabel?: string,
): string {
  const trimmed = fileName.trim();
  if (trimmed && !isUuidLikeFileName(trimmed)) {
    const dot = trimmed.lastIndexOf('.');
    const base = dot > 0 ? trimmed.slice(0, dot) : trimmed;
    const ext = dot > 0 ? trimmed.slice(dot) : '';
    if (TOKEN_LABEL.test(base)) {
      return sanitizeOriginalFileName(`${toSentenceCase(base)}${ext}`);
    }
    return sanitizeOriginalFileName(trimmed);
  }

  const label = fallbackLabel?.trim();
  if (label) {
    const ext =
      trimmed && trimmed.includes('.')
        ? trimmed.slice(trimmed.lastIndexOf('.'))
        : '';
    const sentence = toSentenceCase(label) || 'Upload';
    return sanitizeOriginalFileName(ext ? `${sentence}${ext}` : sentence);
  }

  return sanitizeOriginalFileName(trimmed || 'upload');
}

/**
 * Prefer the browser-selected file name for booking document registration.
 * Never substitutes API document-type tokens (e.g. CO_OWNER_PAN_CARD).
 */
export function resolvePropertyOwnerUploadOriginalFileName(
  file: File,
  humanFallbackLabel: string,
): string {
  const trimmed = file.name?.trim() ?? '';
  if (trimmed && !isUuidLikeFileName(trimmed)) {
    const dot = trimmed.lastIndexOf('.');
    const base = dot > 0 ? trimmed.slice(0, dot) : trimmed;
    if (!TOKEN_LABEL.test(base)) {
      return sanitizeOriginalFileName(trimmed);
    }
  }

  return resolveUploadDisplayName(trimmed, humanFallbackLabel);
}
